import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Calendar, CheckCircle, Clock, AlertCircle, Plus, XCircle, Trash2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { validateDateBR } from '../lib/helpers';

const API = process.env.REACT_APP_BACKEND_URL;

const SolicitarFeriasPage = () => {
  const [loading, setLoading] = useState(true);
  const [meusPeriodos, setMeusPeriodos] = useState([]);
  const [minhasSolicitacoes, setMinhasSolicitacoes] = useState([]);
  const [showNovaModal, setShowNovaModal] = useState(false);
  const [periodoSelecionado, setPeriodoSelecionado] = useState(null);
  
  const [formSolicitacao, setFormSolicitacao] = useState({
    start_date: '',
    end_date: '',
    days_of_enjoyment: 30,
    notes: '',
    sell_vacation_days: false,
    days_to_sell: 0,
    request_13th_advance: false
  });

  useEffect(() => {
    carregarDados();
  }, []);

  const [employeeId, setEmployeeId] = useState(null);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Carregar períodos
      const periodosRes = await axios.get(`${API}/api/vacations/my-periods`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMeusPeriodos(periodosRes.data.periods || []);
      setEmployeeId(periodosRes.data.employee_id); // Salvar employee_id
      
      // Carregar solicitações
      const solicitacoesRes = await axios.get(`${API}/api/vacations/my-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMinhasSolicitacoes(solicitacoesRes.data || []);
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNovaSolicitacao = (periodo) => {
    setPeriodoSelecionado(periodo);
    setFormSolicitacao({
      start_date: '',
      end_date: '',
      days_of_enjoyment: 30,
      notes: '',
      sell_vacation_days: false,
      days_to_sell: 0,
      request_13th_advance: false
    });
    setShowNovaModal(true);
  };

  // Calcular dias automaticamente
  const calcularDias = (dataInicio, dataFim) => {
    if (!dataInicio || !dataFim) return 0;
    
    const inicio = new Date(dataInicio);
    const fim = new Date(dataFim);
    
    if (fim < inicio) return 0;
    
    const diferencaMilissegundos = fim - inicio;
    const dias = Math.floor(diferencaMilissegundos / (1000 * 60 * 60 * 24)) + 1; // +1 para incluir o último dia
    
    return dias;
  };

  // Atualizar dias quando as datas mudarem
  useEffect(() => {
    if (formSolicitacao.start_date && formSolicitacao.end_date) {
      const diasCalculados = calcularDias(formSolicitacao.start_date, formSolicitacao.end_date);
      setFormSolicitacao(prev => ({...prev, days_of_enjoyment: diasCalculados}));
    }
  }, [formSolicitacao.start_date, formSolicitacao.end_date]);

  const enviarSolicitacao = async () => {
    // Validações
    if (!formSolicitacao.start_date || !formSolicitacao.end_date) {
      alert('Por favor, preencha as datas de início e fim');
      return;
    }
    if (!validateDateBR(formSolicitacao.start_date, { pastYears: 15, futureYears: 15 }) || !validateDateBR(formSolicitacao.end_date, { pastYears: 15, futureYears: 15 })) {
      toast.error('Data de início ou fim inválida');
      return;
    }
    if (formSolicitacao.end_date < formSolicitacao.start_date) {
      toast.error('A data fim não pode ser menor que a data início');
      return;
    }

    const acquisitionEnd = periodoSelecionado?.acquisition_end?.split('T')[0];
    if (acquisitionEnd && formSolicitacao.start_date <= acquisitionEnd) {
      toast.error(`As férias só podem começar após ${formatDate(acquisitionEnd)}`);
      return;
    }

    if (formSolicitacao.days_of_enjoyment > periodoSelecionado.days_remaining) {
      alert(`Você não pode solicitar ${formSolicitacao.days_of_enjoyment} dias. Você tem apenas ${periodoSelecionado.days_remaining} dias disponíveis.`);
      return;
    }

    if (formSolicitacao.days_of_enjoyment < 1) {
      alert('A solicitação deve ter pelo menos 1 dia');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        employee_id: employeeId,
        vacation_period_id: periodoSelecionado.id,
        ...formSolicitacao
      };
      
      console.log('📤 Enviando solicitação:', payload);
      
      const response = await axios.post(
        `${API}/api/vacations/requests`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('✅ Resposta do servidor:', response.data);
      
      alert('Solicitação enviada com sucesso! Aguarde aprovação do RH.');
      setShowNovaModal(false);
      carregarDados();
      
    } catch (error) {
      console.error('❌ Erro completo:', error);
      console.error('❌ Resposta do erro:', error.response?.data);
      alert(error.response?.data?.detail || 'Erro ao enviar solicitação');
    }
  };

  const handleExcluirSolicitacao = async (solicitacaoId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta solicitação pendente?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/vacations/requests/${solicitacaoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Solicitação cancelada com sucesso');
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir solicitação');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pendente', color: 'bg-yellow-500' },
      approved: { label: 'Aprovado', color: 'bg-green-500' },
      rejected: { label: 'Rejeitado', color: 'bg-red-500' }
    };
    
    const config = statusConfig[status] || { label: status, color: 'bg-gray-500' };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  const getPeriodStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Disponível', color: 'bg-green-500' },
      expired: { label: 'Vencido', color: 'bg-red-500' },
      pending: { label: 'Aguardando', color: 'bg-gray-500' }
    };
    
    const config = statusConfig[status] || { label: status, color: 'bg-gray-500' };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const periodosDisponiveis = meusPeriodos.filter(
    p => ['active', 'pending'].includes(p.status) && p.days_remaining > 0
  );
  const periodosUsados = meusPeriodos.filter(p => p.days_remaining === 0);
  const solicitacoesPendentes = minhasSolicitacoes.filter(s => s.status === 'pending');
  const solicitacoesAprovadas = minhasSolicitacoes.filter(s => s.status === 'approved');

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Solicitar Férias</h1>
          <p className="text-gray-600 mt-1">Gerencie suas férias e faça novas solicitações</p>
        </div>
      </div>

      <Tabs defaultValue="disponiveis" className="w-full">
        <TabsList className="flex w-full items-center justify-start gap-1 overflow-x-auto sm:grid sm:grid-cols-3 sm:overflow-x-visible">
          <TabsTrigger value="disponiveis" className="flex-shrink-0">
            Períodos Disponíveis ({periodosDisponiveis.length})
          </TabsTrigger>
          <TabsTrigger value="solicitacoes" className="flex-shrink-0">
            Minhas Solicitações ({minhasSolicitacoes.length})
          </TabsTrigger>
          <TabsTrigger value="historico" className="flex-shrink-0">
            Histórico ({periodosUsados.length})
          </TabsTrigger>
        </TabsList>

        {/* Aba: Períodos Disponíveis */}
        <TabsContent value="disponiveis" className="space-y-4">
          <Card>
            <CardHeader className="bg-gradient-to-r from-green-500 to-green-600 text-white">
              <CardTitle>Períodos Disponíveis para Solicitação</CardTitle>
              <CardDescription className="text-green-100">
                Períodos de férias que você pode solicitar agora
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {periodosDisponiveis.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-600 text-lg">Nenhum período disponível no momento</p>
                  <p className="text-gray-500 text-sm mt-2">
                    Aguarde completar um novo período aquisitivo
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {periodosDisponiveis.map((periodo) => (
                    <div key={periodo.id} className="border-2 border-green-200 rounded-lg p-5 bg-green-50">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-lg">Período Aquisitivo</h3>
                          <p className="text-gray-700">
                            {formatDate(periodo.acquisition_start)} - {formatDate(periodo.acquisition_end)}
                          </p>
                        </div>
                        {getPeriodStatusBadge(periodo.status)}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4 text-sm">
                        <div className="bg-white p-3 rounded-lg">
                          <p className="text-gray-600">Período Concessivo</p>
                          <p className="font-semibold">
                            {formatDate(periodo.concession_start)} - {formatDate(periodo.concession_end)}
                          </p>
                        </div>
                        <div className="bg-white p-3 rounded-lg">
                          <p className="text-gray-600">Dias Disponíveis</p>
                          <p className="font-semibold text-green-600 text-xl">
                            {periodo.days_remaining} dias
                          </p>
                        </div>
                        <div className="bg-white p-3 rounded-lg">
                          <p className="text-gray-600">Prazo Final</p>
                          <p className="font-semibold">
                            {formatDate(periodo.concession_end)}
                          </p>
                        </div>
                      </div>

                      <Button
                        onClick={() => handleNovaSolicitacao(periodo)}
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Nova Solicitação
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba: Minhas Solicitações */}
        <TabsContent value="solicitacoes" className="space-y-4">
          <Card>
            <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
              <CardTitle>Minhas Solicitações de Férias</CardTitle>
              <CardDescription className="text-blue-100">
                Acompanhe o status das suas solicitações
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {minhasSolicitacoes.length === 0 ? (
                <div className="text-center py-12">
                  <AlertCircle className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-600 text-lg">Nenhuma solicitação ainda</p>
                  <p className="text-gray-500 text-sm mt-2">
                    Faça sua primeira solicitação em "Períodos Disponíveis"
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Solicitações Pendentes */}
                  {solicitacoesPendentes.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                        <Clock className="h-5 w-5 text-yellow-600" />
                        Aguardando Aprovação ({solicitacoesPendentes.length})
                      </h3>
                      <div className="space-y-3">
                        {solicitacoesPendentes.map((sol) => (
                          <div key={sol.id} className="border-2 border-yellow-200 bg-yellow-50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <p className="font-semibold">
                                  {formatDate(sol.start_date)} - {formatDate(sol.end_date)}
                                </p>
                                <p className="text-sm text-gray-600">{sol.days_requested} dias de férias</p>
                              </div>
                              <div className="flex items-center gap-2">
                                {getStatusBadge(sol.status)}
                                <button
                                  onClick={() => handleExcluirSolicitacao(sol.id)}
                                  className="text-red-500 hover:text-red-700 transition-colors"
                                  title="Excluir solicitação"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                            {sol.notes && (
                              <p className="text-sm text-gray-700 mt-2">
                                💬 {sol.notes}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Solicitações Aprovadas */}
                  {solicitacoesAprovadas.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        Aprovadas ({solicitacoesAprovadas.length})
                      </h3>
                      <div className="space-y-3">
                        {solicitacoesAprovadas.map((sol) => (
                          <div key={sol.id} className="border-2 border-green-200 bg-green-50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <p className="font-semibold">
                                  {formatDate(sol.start_date)} - {formatDate(sol.end_date)}
                                </p>
                                <p className="text-sm text-gray-600">{sol.days_requested} dias de férias</p>
                              </div>
                              {getStatusBadge(sol.status)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba: Histórico */}
        <TabsContent value="historico" className="space-y-4">
          <Card>
            <CardHeader className="bg-gradient-to-r from-gray-500 to-gray-600 text-white">
              <CardTitle>Histórico de Férias</CardTitle>
              <CardDescription className="text-gray-100">
                Períodos de férias já utilizados
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {periodosUsados.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-600 text-lg">Nenhum histórico ainda</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {periodosUsados.map((periodo) => (
                    <div key={periodo.id} className="border rounded-lg p-4 bg-gray-50">
                      <p className="font-semibold">
                        Período: {formatDate(periodo.acquisition_start)} - {formatDate(periodo.acquisition_end)}
                      </p>
                      <p className="text-sm text-gray-600">
                        {periodo.days_earned} dias utilizados
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal: Nova Solicitação */}
      <Dialog open={showNovaModal} onOpenChange={setShowNovaModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nova Solicitação de Férias</DialogTitle>
            <DialogDescription>
              Preencha as informações para solicitar suas férias
            </DialogDescription>
          </DialogHeader>

          {periodoSelecionado && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="font-semibold mb-2">Período Selecionado:</p>
                <p className="text-sm">
                  Aquisitivo: {formatDate(periodoSelecionado.acquisition_start)} - {formatDate(periodoSelecionado.acquisition_end)}
                </p>
                <p className="text-sm text-green-600 font-medium">
                  Dias disponíveis: {periodoSelecionado.days_remaining}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">Data Início</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formSolicitacao.start_date}
                    onChange={(e) => setFormSolicitacao({...formSolicitacao, start_date: e.target.value})}
                    min={(() => {
                      const acquisitionEnd = periodoSelecionado.acquisition_end?.split('T')[0];
                      if (!acquisitionEnd) return periodoSelecionado.concession_start?.split('T')[0];
                      const date = new Date(`${acquisitionEnd}T12:00:00`);
                      date.setDate(date.getDate() + 1);
                      return date.toISOString().split('T')[0];
                    })()}
                    max={periodoSelecionado.concession_end?.split('T')[0]}
                  />
                </div>
                <div>
                  <Label htmlFor="end_date">Data Fim</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formSolicitacao.end_date}
                    onChange={(e) => setFormSolicitacao({...formSolicitacao, end_date: e.target.value})}
                    min={formSolicitacao.start_date}
                    max={periodoSelecionado.concession_end?.split('T')[0]}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="days">Dias de Gozo (calculado automaticamente)</Label>
                <Input
                  id="days"
                  type="number"
                  value={formSolicitacao.days_of_enjoyment}
                  readOnly
                  className="bg-gray-100 cursor-not-allowed"
                />
                {formSolicitacao.days_of_enjoyment > periodoSelecionado.days_remaining && (
                  <p className="text-red-600 text-sm mt-1">
                    ⚠️ Atenção: Você está solicitando mais dias do que tem disponível ({periodoSelecionado.days_remaining} dias)
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="notes">Observações (Opcional)</Label>
                <textarea
                  id="notes"
                  className="w-full p-2 border rounded-md"
                  rows="3"
                  value={formSolicitacao.notes}
                  onChange={(e) => setFormSolicitacao({...formSolicitacao, notes: e.target.value})}
                  placeholder="Informações adicionais sobre sua solicitação..."
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formSolicitacao.sell_vacation_days}
                    onChange={(e) => setFormSolicitacao({...formSolicitacao, sell_vacation_days: e.target.checked})}
                  />
                  <span>Vender parte das férias (abono pecuniário)</span>
                </label>

                {formSolicitacao.sell_vacation_days && (
                  <div className="ml-6">
                    <Label htmlFor="days_to_sell">Dias para vender (máx. 10)</Label>
                    <Input
                      id="days_to_sell"
                      type="number"
                      min="1"
                      max="10"
                      value={formSolicitacao.days_to_sell}
                      onChange={(e) => setFormSolicitacao({...formSolicitacao, days_to_sell: parseInt(e.target.value)})}
                    />
                  </div>
                )}

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formSolicitacao.request_13th_advance}
                    onChange={(e) => setFormSolicitacao({...formSolicitacao, request_13th_advance: e.target.checked})}
                  />
                  <span>Solicitar adiantamento do 13º salário</span>
                </label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovaModal(false)}>
              Cancelar
            </Button>
            <Button onClick={enviarSolicitacao} className="bg-green-600 hover:bg-green-700">
              Enviar Solicitação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SolicitarFeriasPage;
